/* eslint-disable camelcase */
/**
 * Public ad / analytics ids for the Aperio booking portal (shared tracking
 * contract; the marketing site keeps the same keys in its
 * `src/static/js/config.js`). These are PUBLIC ids, never secrets.
 *
 * Activated 2026-09-25 with the owner's ids for GA4, Google Ads, Meta,
 * Microsoft UET and Nextdoor, and 2026-09-26 for the LinkedIn Insight Tag
 * (the same values as the site's config.js). Every ad tag, LinkedIn included,
 * loads only with advertising consent (aperioTracking.ts). An empty id means
 * that vendor is off: no script is loaded and nothing is sent. Only the
 * Aperio preset ever reads this file (util/aperioTracking.ts); Go Oil never
 * does.
 *
 * When an id is filled in, also add the vendor's origins to the
 * Content-Security-Policy in client/serve.json (today Report-Only), e.g.
 * bat.bing.com, ads.nextdoor.com, snap.licdn.com / *.ads.linkedin.com.
 */
export interface AperioTrackingIds {
  /** GA4 measurement id, `G-XXXXXXX`. */
  ga4Id: string;
  /** Google Ads account, `AW-XXXXXXXXX`. */
  googleAdsId: string;
  /** Google Ads conversion labels, per contract event. */
  googleAdsLabels: {
    purchase: string;
    begin_checkout: string;
    address_serviceable: string;
    generate_lead: string;
    phone_click: string;
    /** "Aperio – Fleet form sent" (secondary): the free fleet sign-up. */
    fleet_lead?: string;
  };
  metaPixelId: string;
  /** Microsoft Advertising UET tag id. */
  uetTagId: string;
  nextdoorPixelId: string;
  linkedinPartnerId: string;
  linkedinLeadConversionId: string;
  /** OpenAI (ChatGPT) Ads pixel id. */
  openaiPixelId: string;
}

export const APERIO_TRACKING_IDS: AperioTrackingIds = {
  ga4Id: 'G-ZRK9QM96WT',
  googleAdsId: 'AW-18475142980',
  googleAdsLabels: {
    purchase: '5ofeCPO9vYUdEMSe0elE',
    begin_checkout: 'ZSqgCPa9vYUdEMSe0elE',
    address_serviceable: 'CLKoCPm9vYUdEMSe0elE',
    generate_lead: '0fkTCPy9vYUdEMSe0elE',
    phone_click: 'W4G9CP-9vYUdEMSe0elE',
    fleet_lead: 'jtk1CPHJhawdEMSe0elE',
  },
  metaPixelId: '963784490100953',
  uetTagId: '187276742',
  nextdoorPixelId: 'd38d2d1d-d106-4279-9bc3-e75c25f163aa',
  // LinkedIn Insight Tag (2026-09-26): page view with advertising consent.
  linkedinPartnerId: '10075740',
  // No conversion id issued yet: empty = no LinkedIn conversion is sent.
  linkedinLeadConversionId: '',
  // OpenAI (ChatGPT) Ads pixel (owner, 2026-10-20): advertising consent only.
  openaiPixelId: 'YWWKdjVLjtb8DcP81d5FrE',
};

/**
 * Nextdoor pixel event names, per contract event. PURCHASE, INITIATE_CHECKOUT,
 * LEAD and PAGE_VIEW are Nextdoor standard events; the two custom slots are
 * the ones the owner's Nextdoor account keeps for our other conversions. The
 * marketing site maps the same way (aperio/site/src/static/js/ads.js). Kept
 * here so they can be corrected without touching the tracking code.
 */
export const NEXTDOOR_EVENTS = {
  pageView: 'PAGE_VIEW',
  purchase: 'PURCHASE',
  begin_checkout: 'INITIATE_CHECKOUT',
  generate_lead: 'LEAD',
  address_serviceable: 'CUSTOM_CONVERSION_1',
  phone_click: 'CUSTOM_CONVERSION_2',
};

/**
 * OpenAI (ChatGPT) Ads pixel conversion names: the three conversion events the
 * owner's Ads Manager is set up with, and the "type" (data shape) OpenAI's
 * JavaScript Pixel expects for each. Sent as
 * `oaiq('measure', name, { type, amount, currency }, { event_id })`, with
 * `amount` in cents. Kept here so they can be corrected without touching the
 * tracking code.
 */
export const OPENAI_EVENTS = {
  /** Every booking, on the confirmation page. */
  appointment_scheduled: { name: 'appointment_scheduled', type: 'customer_action' },
  /** A booking that includes a plan (Yearly / Monthly / All-Inclusive), as well. */
  subscription_created: { name: 'subscription_created', type: 'plan_enrollment' },
  /** A lead form sent (the marketing site's; in the portal, the free fleet sign-up). */
  lead_created: { name: 'lead_created', type: 'customer_action' },
  /**
   * Micro-conversions (owner, 2026-10-27), sent as OpenAI custom events:
   * `oaiq('measure', 'custom', {type: 'custom'}, {custom_event_name})`.
   * The marketing site sends the same names (ads.js).
   */
  address_checked: { name: 'address_checked', type: 'custom' },
  call_tapped: { name: 'call_tapped', type: 'custom' },
} as const;
